import { ReactNode, useState, useRef, useEffect } from "react";

interface TooltipProps {
  content: string;
  children: ReactNode;
  className?: string;
  maxWidth?: string;
}

export function Tooltip({ content, children, className = "", maxWidth = "w-80" }: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<'top' | 'bottom'>('top');
  const [horizontalOffset, setHorizontalOffset] = useState(0);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      
      // Check vertical positioning
      const spaceAbove = triggerRect.top;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      
      if (spaceAbove < tooltipRect.height + 10) {
        setPosition('bottom');
      } else {
        setPosition('top');
      }
      
      // Check horizontal positioning
      const triggerCenter = triggerRect.left + triggerRect.width / 2;
      const tooltipHalfWidth = tooltipRect.width / 2;
      
      let offset = 0;
      
      // If tooltip would overflow on the right
      if (triggerCenter + tooltipHalfWidth > viewportWidth - 10) {
        offset = viewportWidth - 10 - (triggerCenter + tooltipHalfWidth);
      }
      // If tooltip would overflow on the left
      else if (triggerCenter - tooltipHalfWidth < 10) {
        offset = 10 - (triggerCenter - tooltipHalfWidth);
      }
      
      // Additional check: ensure tooltip doesn't go beyond the left edge of viewport
      const tooltipLeftEdge = triggerCenter - tooltipHalfWidth + offset;
      if (tooltipLeftEdge < 10) {
        offset = 10 - (triggerCenter - tooltipHalfWidth);
      }
      
      setHorizontalOffset(offset);
    }
  }, [isVisible]);

  return (
    <div 
      ref={triggerRef}
      className={`relative inline-block ${className}`}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      
      {isVisible && (
        <div
          ref={tooltipRef}
          className={`
            absolute left-1/2 px-3 py-2 
            bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg 
            shadow-lg transition-opacity duration-200 pointer-events-none 
            z-[9999] ${maxWidth} text-left
            ${position === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'}
          `}
          style={{
            transform: `translateX(calc(-50% + ${horizontalOffset}px))`,
          }}
        >
          <div className="whitespace-normal">
            {content}
          </div>
          {/* Arrow */}
          <div 
            className={`
              absolute left-1/2 border-4 border-transparent
              ${position === 'top' 
                ? 'top-full border-t-gray-900 dark:border-t-gray-700' 
                : 'bottom-full border-b-gray-900 dark:border-b-gray-700'
              }
            `}
            style={{
              transform: `translateX(calc(-50% - ${horizontalOffset}px))`,
            }}
          />
        </div>
      )}
    </div>
  );
}

interface InfoIconProps {
  tooltip: string;
  className?: string;
}

export function InfoIcon({ tooltip, className = "" }: InfoIconProps) {
  return (
    <Tooltip content={tooltip} className={`ml-2 ${className}`}>
      <svg 
        className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" 
        viewBox="0 0 20 20" 
        fill="currentColor"
      >
        <path 
          fillRule="evenodd" 
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" 
          clipRule="evenodd" 
        />
      </svg>
    </Tooltip>
  );
}
